/**
 * App-shell wrapper for the non-game authed pages (History, Account, Admin): a slim header with the
 * wordmark, primary nav, the signed-in email, and sign-out. The game itself renders full-screen
 * without this shell (it has its own HUD).
 */
import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { useLogout } from "../api/auth";
import { cx } from "../lib/ui";
import logoRed from "../assets/brand/logo-borenius-red.svg";

export function PageShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();

  const navItem = (to: string, label: string) => (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        cx(
          "rounded-sm px-3 py-1.5 text-sm",
          isActive ? "bg-ink-100 font-medium text-ink" : "text-ink-600 hover:text-ink",
        )
      }
    >
      {label}
    </NavLink>
  );

  return (
    <div className="min-h-svh bg-paper">
      <header className="sticky top-0 z-20 border-b border-ink-150 bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-2 gap-y-2 px-4 py-3 sm:px-6">
          <NavLink to="/" className="shrink-0" aria-label="Borenius — home">
            <img src={logoRed} alt="Borenius" className="h-5 w-auto" />
          </NavLink>
          <nav className="ml-1 flex items-center gap-0.5 sm:ml-3">
            {navItem("/", "Play")}
            {navItem("/history", "History")}
            {navItem("/account", "Account")}
            {user?.kind === "admin" && navItem("/admin", "Admin")}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {user && <span className="hidden text-sm text-ink-500 sm:inline">{user.email}</span>}
            <button
              onClick={async () => {
                await logout.mutateAsync();
                navigate("/", { replace: true });
              }}
              className="text-sm text-ink-600 hover:text-brand"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">{children}</main>
    </div>
  );
}
